import styles from "../CommonFields/CommonFields.module.css";
import { BlockStack, Select, Text, TextField, Card } from "@shopify/polaris";
import { MdOutlineTrendingUp, MdOutlineBlock } from "react-icons/md";
import { DISCOUNT_TYPES, DiscountTypes, getDiscountOptions } from "../constants";
import { DiscountIcon } from "@shopify/polaris-icons";
import { PricingStrategyTile } from "../CommonFields/Discount";
import { getNumberFieldSuffix } from "../../../utilities/constants";
import { Field } from "@shopify/react-form";
import BBTierDiscounts from "../CommonFields/BBTierDiscounts";
import { BBTierOptions } from "../Types";

const PlaceholderDiscount = ({
    dispatchBundleConfig,
    discount_type,
    lastStandardType,
    money_format,
    discount_value,
    bundleType,
    trackBundleDiscount,
    hide_prices_product_card,
    discount_options,
    tier_discount_basis,
    show_progress_bar,
    products,
    setLastStandardType,
}: {
    dispatchBundleConfig: any;
    discount_type: Field<DiscountTypes>;
    lastStandardType: DiscountTypes;
    money_format: string;
    discount_value: Field<number>;
    bundleType: string;
    trackBundleDiscount: any;
    hide_prices_product_card?: Field<boolean>;
    discount_options?: Field<BBTierOptions[]>;
    tier_discount_basis?: Field<any>;
    show_progress_bar?: any;
    products?: any;
    setLastStandardType: (value: DiscountTypes) => void;
}) => {
    return (
        <>
            <BlockStack gap="300">
                <Text as="h3" variant="headingSm" fontWeight="bold">
                    1. Choose Pricing Strategy
                </Text>
                <div className={styles.tilesGrid}>
                    <PricingStrategyTile
                        title="Tiered"
                        description="Buy more, save more"
                        icon={MdOutlineTrendingUp}
                        selected={discount_type?.value == "tier"}
                        onClick={() => {
                            dispatchBundleConfig({
                                payload: "tier",
                                key: "discount_type",
                            });
                            discount_type.onChange("tier" as unknown as DiscountTypes);
                        }}
                    />

                    <PricingStrategyTile
                        title="Single"
                        description="Build & save"
                        icon={DiscountIcon}
                        selected={
                            discount_type.value !== "tier" &&
                            discount_type.value !== DISCOUNT_TYPES.FIXED_BOXES &&
                            discount_type.value !== DISCOUNT_TYPES.NONE
                        }
                        onClick={() => {
                            const payload =
                                lastStandardType === DISCOUNT_TYPES.NONE ? DISCOUNT_TYPES.PERCENTAGE : lastStandardType;
                            dispatchBundleConfig({
                                payload,
                                key: "discount_type",
                            });
                            discount_type.onChange(payload);
                        }}
                    />

                    <PricingStrategyTile
                        title="No Discount"
                        description="Standard pricing"
                        icon={MdOutlineBlock}
                        selected={discount_type.value === DISCOUNT_TYPES.NONE}
                        onClick={() => {
                            dispatchBundleConfig({
                                payload: DISCOUNT_TYPES.NONE,
                                key: "discount_type",
                            });
                            discount_type.onChange(DISCOUNT_TYPES.NONE);
                        }}
                    />
                </div>
            </BlockStack>
            {discount_type.value === DISCOUNT_TYPES.TIER ? (
                <div
                    className={`${styles.formRow} `}
                    style={{
                        gridTemplateColumns: "1fr",
                    }}
                >
                    <BBTierDiscounts
                        dispatchBundleConfig={dispatchBundleConfig}
                        discount_options={discount_options as Field<BBTierOptions[]>}
                        products={products}
                        tier_discount_basis={tier_discount_basis as any}
                        show_progress_bar={show_progress_bar}
                        bundleType={bundleType}
                    />
                </div>
            ) : discount_type.value === DISCOUNT_TYPES.NONE ? (
                <></>
            ) : (
                <>
                    <BlockStack gap="200">
                        <Text as="h3" variant="headingSm" fontWeight="bold">
                            2. Configure Offer
                        </Text>

                        <Card background="bg-surface-secondary">
                            <div className={`${styles.formRow} `}>
                                <Select
                                    label={"Discount Type"}
                                    placeholder={"Select discount type"}
                                    options={getDiscountOptions(money_format.split("{{")[0].trim()).filter(
                                        (option) => option.value !== DISCOUNT_TYPES.NONE,
                                    )}
                                    onBlur={() => discount_type.runValidation()}
                                    onChange={(val) => {
                                        dispatchBundleConfig({
                                            payload: val,
                                            key: "discount_type",
                                        });
                                        trackBundleDiscount({
                                            cta: "Discount",
                                            type: val,
                                            value: discount_value.value?.toString(),
                                            bundleType: bundleType,
                                        });
                                        discount_type.onChange(val as unknown as DiscountTypes);
                                        setLastStandardType(val as DiscountTypes);
                                        if (hide_prices_product_card) {
                                            dispatchBundleConfig({
                                                payload: false,
                                                key: "hide_prices_product_card",
                                            });
                                            hide_prices_product_card.onChange(false);
                                        }
                                    }}
                                    value={discount_type.value}
                                />
                                {getNumberFieldSuffix(discount_type.value, money_format).show && (
                                    <TextField
                                        type={"number"}
                                        label={"Value"}
                                        placeholder="value"
                                        min={0}
                                        suffix={getNumberFieldSuffix(discount_type.value, money_format).suffix}
                                        {...discount_value}
                                        value={discount_value.value.toString()}
                                        autoComplete="off"
                                        onBlur={() => {
                                            discount_value.runValidation();
                                            trackBundleDiscount({
                                                cta: "Discount",
                                                value: discount_value.value?.toString(),
                                                type: discount_type.value,
                                                bundleType: bundleType,
                                            });
                                        }}
                                        onChange={(val) => {
                                            const regex = /^(\d+(\.\d{0,2})?)?$/;
                                            if (regex.test(val)) {
                                                if (discount_type.value === "percentage" && +val > 100) {
                                                    val = "100";
                                                }

                                                dispatchBundleConfig({
                                                    payload: +val,
                                                    key: "discount_value",
                                                });

                                                discount_value.onChange(val as unknown as number);
                                            }
                                        }}
                                    />
                                )}
                            </div>
                        </Card>
                    </BlockStack>
                </>
            )}
        </>
    );
};

export default PlaceholderDiscount;
